import React, { useEffect, useState, useRef } from 'react';
import { Users, BookOpen, MessageSquare, TrendingUp, ArrowUpRight, ArrowDownRight, Sparkles } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { adminService, AdminStats as AdminStatsType } from '../../services/adminService';
import { useThemeStore } from '../../stores/themeStore';

// Animated counter hook
const useCountUp = (end: number, duration: number = 1500, startOnView: boolean = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
    }
  }, [startOnView]);

  useEffect(() => {
    if (startOnView && ref.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasStarted) {
            setHasStarted(true);
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(ref.current);
      return () => observer.disconnect();
    }
  }, [hasStarted, startOnView]);

  useEffect(() => {
    if (!hasStarted || end === 0) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, hasStarted]);

  return { count, ref };
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  trend: string;
  gradient: string;
  iconBg: string;
  delay: number;
  isDark: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, trend, gradient, iconBg, delay, isDark }) => {
  const { count, ref } = useCountUp(value);
  const isPositive = trend?.startsWith('+');
  const trendValue = trend?.replace(/[+-]/, '') || '0%';

  return (
    <div 
      ref={ref}
      className="relative group"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={`absolute -inset-0.5 ${gradient} rounded-2xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500`} />
      
      <div className={`relative backdrop-blur-sm rounded-2xl border p-6 overflow-hidden transition-all duration-300 group-hover:-translate-y-1 ${
        isDark 
          ? 'bg-white/5 border-white/10' 
          : 'bg-white border-slate-200 shadow-sm'
      }`}>
        <div className={`absolute -top-10 -right-10 w-32 h-32 ${gradient} rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity`} />
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300`}>
              {icon}
            </div>
            
            <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
              isPositive 
                ? 'bg-emerald-500/10 text-emerald-500' 
                : 'bg-red-500/10 text-red-500'
            }`}>
              {isPositive ? (
                <ArrowUpRight className="w-3 h-3" />
              ) : (
                <ArrowDownRight className="w-3 h-3" />
              )}
              {trendValue}
            </div>
          </div>

          <div className="mb-1">
            <span className={`text-3xl font-bold tabular-nums ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {count.toLocaleString()}
            </span>
          </div>

          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>

          <div className={`mt-4 h-1 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
            <div 
              className={`h-full ${gradient} rounded-full transition-all duration-1000 ease-out`}
              style={{ width: `${Math.min((count / (value * 1.2)) * 100, 100)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminStats: React.FC = () => {
  const theme = useThemeStore((state) => state.theme);
  const isDark = theme === 'dark';

  const { data: statsData, isLoading, error } = useQuery({
    queryKey: ['adminStats'],
    queryFn: adminService.getStats
  });

  const statsConfig = [
    { 
      key: 'total_users', 
      icon: <Users className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />, 
      label: 'Total Users',
      gradient: 'bg-gradient-to-r from-cyan-500 to-blue-500',
      iconBg: isDark ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20' : 'bg-gradient-to-br from-cyan-100 to-blue-100'
    },
    { 
      key: 'active_subjects', 
      icon: <BookOpen className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />, 
      label: 'Active Subjects',
      gradient: 'bg-gradient-to-r from-violet-500 to-purple-500',
      iconBg: isDark ? 'bg-gradient-to-br from-violet-500/20 to-purple-500/20' : 'bg-gradient-to-br from-violet-100 to-purple-100'
    },
    { 
      key: 'daily_chats', 
      icon: <MessageSquare className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />, 
      label: 'Daily Chats',
      gradient: 'bg-gradient-to-r from-amber-500 to-orange-500',
      iconBg: isDark ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20' : 'bg-gradient-to-br from-amber-100 to-orange-100'
    },
    { 
      key: 'user_growth', 
      icon: <TrendingUp className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />, 
      label: 'User Growth',
      gradient: 'bg-gradient-to-r from-emerald-500 to-teal-500',
      iconBg: isDark ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20' : 'bg-gradient-to-br from-emerald-100 to-teal-100'
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div 
            key={i} 
            className={`backdrop-blur-sm rounded-2xl border p-6 animate-pulse ${
              isDark 
                ? 'bg-white/5 border-white/10' 
                : 'bg-white border-slate-200'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} />
              <div className={`w-16 h-6 rounded-full ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} />
            </div>
            <div className={`w-20 h-8 rounded mb-2 ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} />
            <div className={`w-24 h-4 rounded ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} />
            <div className={`mt-4 h-1 rounded-full ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-red-400" />
        </div>
        <div>
          <p className="text-red-500 font-medium">Error loading statistics</p>
          <p className="text-sm text-red-400">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {statsConfig.map(({ key, icon, label, gradient, iconBg }, index) => {
        const stat = statsData?.[key as keyof AdminStatsType];
        return (
          <StatCard
            key={key}
            icon={icon}
            label={label}
            value={typeof stat?.value === 'number' ? stat.value : parseInt(String(stat?.value || 0), 10) || 0}
            trend={stat?.trend || '+0%'}
            gradient={gradient}
            iconBg={iconBg}
            delay={index * 100}
            isDark={isDark}
          />
        );
      })}
    </div>
  );
};

export default AdminStats;
